// src/data/affectedMembers.js
//
// RULING 270 (2026-10-02): OTHERS AFFECTED ARE NOTIFIED AND GIVEN A CHANCE TO OBJECT - AND THE RECORD SAYS SO.
//
// Where a claim would affect another member (seniority, a position, a vacation choice, pay), the record carries a
// box: "Members affected were notified and given an opportunity to be heard," with who, when and how.
//
// READ WITH M-3 (members adverse in the same matter): this module RECORDS THE NOTICE and nothing else. Who
// represents whom is M-3's, and M-3 is resolved by each General Chairperson (Bob, 2026-09-17) - nothing here
// assigns, recommends or infers representation, and nothing here decides WHETHER a claim affects another member.
// The officer who records the notice says so; the platform does not guess it.
//
// What already existed (PD-10): the claim names "the other employee" who did the work (violatorName /
// violatorRosterNumber, claimPayload.js). That is a fact on the record and is SHOWN beside the box; it is never
// read as "a notice was owed" or "a notice was given".
//
// Stored on the claim as an append-only array `affectedMembersNotice` (arrayUnion), written by the officers the
// claims update rule already permits (lcEdit / gcEdit / adminEdit) - no new rule, the same way appealActs is
// written. Withdrawable by number: RULING_270_AFFECTED_NOTICE false hides the box and records nothing.
//
// Pure module: no React, no Firestore.

export const RULING_270_AFFECTED_NOTICE = true;

// The box's sentence, exactly as ruling 270 words it.
export const AFFECTED_NOTICE_SENTENCE = 'Members affected were notified and given an opportunity to be heard.';

// What the claim would affect - ruling 270's own list, and nothing added to it.
export const AFFECTS = Object.freeze([
  Object.freeze({ key: 'seniority', label: 'Seniority' }),
  Object.freeze({ key: 'position', label: 'A position' }),
  Object.freeze({ key: 'vacation', label: 'A vacation choice' }),
  Object.freeze({ key: 'pay', label: 'Pay' }),
]);
const AFFECT_KEYS = AFFECTS.map(a => a.key);

// How the notice was given - the means the platform already uses for service (claimPresentment.js), plus telephone
// as ruling 267's extension record has it. 'other' must say which.
export const NOTICE_MEANS = Object.freeze([
  Object.freeze({ key: 'letter', label: 'Letter' }),
  Object.freeze({ key: 'email', label: 'Email' }),
  Object.freeze({ key: 'in_person', label: 'In person' }),
  Object.freeze({ key: 'telephone', label: 'Telephone' }),
  Object.freeze({ key: 'other', label: 'Another way (say which)' }),
]);
const MEANS_KEYS = NOTICE_MEANS.map(m => m.key);

const s = (v) => (v == null ? '' : String(v)).trim();
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v)) && !Number.isNaN(Date.parse(s(v) + 'T00:00:00Z'));

export function meansLabel(k) {
  const m = NOTICE_MEANS.find(x => x.key === k);
  return m ? m.label : 'Not recorded';
}

// Validate one notice entry. Returns { ok, errors[], record } - never a partial record.
//   input: { members, affects[], notifiedOn, how, howOther, heardOffered, note, today }
export function noticeRecord(input, user) {
  const i = input || {};
  const errors = [];
  const members = s(i.members);
  if (!members) errors.push('Name the member or members who were notified.');
  else if (members.length > 400) errors.push('The names are too long - 400 characters at most.');
  const affects = (Array.isArray(i.affects) ? i.affects : []).map(s).filter(k => AFFECT_KEYS.includes(k));
  if (!affects.length) errors.push('Say what the claim would affect for them: seniority, a position, a vacation choice, or pay.');
  const on = s(i.notifiedOn);
  if (!isDate(on)) errors.push('The date they were notified, as a date.');
  else if (isDate(i.today) && on > s(i.today)) errors.push('That date is in the future. This records a notice that HAS been given.');
  const how = s(i.how);
  if (!MEANS_KEYS.includes(how)) errors.push('Say how they were notified.');
  const howOther = s(i.howOther);
  if (how === 'other' && !howOther) errors.push('"Another way" has to say which.');
  // The box's sentence has two halves - notified AND given an opportunity to be heard. The second is affirmed by the
  // person recording it; it is never assumed from the first.
  if (i.heardOffered !== true) errors.push('Confirm they were given an opportunity to be heard - the record says both, or it says neither.');
  if (errors.length) return { ok: false, errors, record: null };
  return {
    ok: true, errors: [],
    record: {
      members, affects, notifiedOn: on, how, howOther: how === 'other' ? howOther : null,
      heardOffered: true,
      note: s(i.note) || null,
      sentence: AFFECTED_NOTICE_SENTENCE,
      recordedBy: (user && (user.name || user.email)) || 'Unknown',
      recordedByRole: (user && user.role) || 'unknown',
      recordedByUid: (user && user.uid) || '',
      recordedAt: i.at || new Date().toISOString(),
    },
  };
}

// What the box shows. Absence is stated as absence - never as "nobody was affected" and never as "notice was owed".
export function affectedNoticeView(claim) {
  const c = claim || {};
  const list = (Array.isArray(c.affectedMembersNotice) ? c.affectedMembersNotice : [])
    .filter(e => e && s(e.members) && isDate(e.notifiedOn))
    .slice().sort((a, b) => s(a.notifiedOn).localeCompare(s(b.notifiedOn)));
  const other = s(c.violatorName)
    ? s(c.violatorName) + (s(c.violatorRosterNumber) ? ' (roster no. ' + s(c.violatorRosterNumber) + ')' : '')
    : '';
  return {
    recorded: list.length > 0,
    entries: list.map(e => ({
      ...e,
      line: e.members + ' - notified ' + e.notifiedOn + ', ' + (e.how === 'other' ? (e.howOther || 'another way') : meansLabel(e.how)).toLowerCase()
          + '; affects ' + (Array.isArray(e.affects) ? e.affects.map(k => (AFFECTS.find(a => a.key === k) || { label: k }).label.toLowerCase()).join(', ') : '')
          + '. Recorded by ' + s(e.recordedBy) + '.',
    })),
    otherEmployeeOnClaim: other || null,
    emptyText: 'No notice to affected members is recorded on this claim.',
  };
}
